import * as THREE from 'three'
import type { SceneManager } from './SceneManager'
import { setHighlight, getAllThreeObjects } from './ObjectManager'

const BOX_SELECT_THRESHOLD = 5

export interface SelectionBoxBounds {
  left: number
  top: number
  width: number
  height: number
}

type OnSelectCallback = (
  id: string | null,
  screenPos: { x: number; y: number } | null,
  isShift?: boolean,
  event?: MouseEvent
) => void

export type SelectHandlerOptions = {
  onSelectionBox?: (bounds: SelectionBoxBounds | null) => void
  onBoxSelect?: (ids: string[], centerScreen: { x: number; y: number }) => void
  /** 为 true 时不处理选中（如测量模式） */
  shouldBlockSelection?: () => boolean
  /** 点击设备连线（优先于对象；完成连线拖拽目标时由 shouldDeferConnectionPick 跳过） */
  onConnectionClick?: (connectionId: string, e: MouseEvent) => void
  /** 为 true 时不优先拾取连线（例如正在「连接到…」等待点选目标对象） */
  shouldDeferConnectionPick?: () => boolean
}

export class SelectHandler {
  private raycaster = new THREE.Raycaster()
  private _sm: SceneManager
  private _onSelect: OnSelectCallback
  private _currentId: string | null = null
  private _isDragging = false
  private _isBoxSelecting = false
  private _mouseDownOnObject = false
  private _mouseButtonDown = false
  private _altKeyAtMouseDown = false
  private _mouseDownPos = { x: 0, y: 0 }
  private _onSelectionBox?: (b: SelectionBoxBounds | null) => void
  private _onBoxSelect?: (ids: string[], centerScreen: { x: number; y: number }) => void
  private _shouldBlockSelection?: () => boolean
  private _onConnectionClick?: (connectionId: string, e: MouseEvent) => void
  private _shouldDeferConnectionPick?: () => boolean

  constructor(sm: SceneManager, onSelect: OnSelectCallback, options?: SelectHandlerOptions) {
    this._sm = sm
    this._onSelect = onSelect
    this._onSelectionBox = options?.onSelectionBox
    this._onBoxSelect = options?.onBoxSelect
    this._shouldBlockSelection = options?.shouldBlockSelection
    this._onConnectionClick = options?.onConnectionClick
    this._shouldDeferConnectionPick = options?.shouldDeferConnectionPick
    this._bind()
  }

  private _bind() {
    const canvas = this._sm.renderer.domElement
    canvas.addEventListener('mousedown', this._onMouseDown)
    canvas.addEventListener('mouseup', this._onMouseUp)
    canvas.addEventListener('mousemove', this._onMouseMove)
  }

  private _onMouseDown = (e: MouseEvent) => {
    if (this._shouldBlockSelection?.()) return
    if (e.button !== 0) return
    this._mouseButtonDown = true
    this._altKeyAtMouseDown = e.altKey
    this._mouseDownPos = { x: e.clientX, y: e.clientY }
    this._isDragging = false
    this._isBoxSelecting = false
    this._onSelectionBox?.(null)
    const canvas = this._sm.renderer.domElement
    const rect = canvas.getBoundingClientRect()
    const ndc = new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1
    )
    this.raycaster.setFromCamera(ndc, this._sm.camera)
    const hits = this.raycaster.intersectObjects(this._sm.scene.children, true)
    this._mouseDownOnObject = hits.some(
      (h) =>
        h.object.userData.objectId &&
        h.object.userData.interactable !== false &&
        h.object.parent?.name !== '__ground__' &&
        h.object.name !== '__ground__'
    )
  }

  private _onMouseMove = (e: MouseEvent) => {
    if (!this._mouseButtonDown) return
    const dx = e.clientX - this._mouseDownPos.x
    const dy = e.clientY - this._mouseDownPos.y
    if (Math.abs(dx) > BOX_SELECT_THRESHOLD || Math.abs(dy) > BOX_SELECT_THRESHOLD) {
      if (!this._isBoxSelecting && !this._mouseDownOnObject && this._altKeyAtMouseDown) {
        this._isBoxSelecting = true
        this._sm.controls.enabled = false
      }
      this._isDragging = true
      if (this._isBoxSelecting && this._onSelectionBox) {
        const left = Math.min(this._mouseDownPos.x, e.clientX)
        const top = Math.min(this._mouseDownPos.y, e.clientY)
        const width = Math.abs(e.clientX - this._mouseDownPos.x)
        const height = Math.abs(e.clientY - this._mouseDownPos.y)
        this._onSelectionBox({ left, top, width, height })
      }
    }
  }

  private _getIdsInBox(bounds: SelectionBoxBounds): string[] {
    const canvas = this._sm.renderer.domElement
    const rect = canvas.getBoundingClientRect()
    const ids: string[] = []
    const allObjects = getAllThreeObjects()
    const proj = new THREE.Vector3()

    for (const [id, group] of allObjects) {
      if (!group.visible) continue
      const box = new THREE.Box3().setFromObject(group)
      box.getCenter(proj)
      proj.project(this._sm.camera)
      const sx = ((proj.x + 1) / 2) * rect.width + rect.left
      const sy = ((-proj.y + 1) / 2) * rect.height + rect.top
      if (
        sx >= bounds.left &&
        sx <= bounds.left + bounds.width &&
        sy >= bounds.top &&
        sy <= bounds.top + bounds.height
      ) {
        ids.push(id)
      }
    }
    return ids
  }

  private _onMouseUp = (e: MouseEvent) => {
    if (this._shouldBlockSelection?.()) return
    if (e.button !== 0) return
    this._mouseButtonDown = false

    if (this._isBoxSelecting) {
      this._sm.controls.enabled = true
      this._onSelectionBox?.(null)
      const left = Math.min(this._mouseDownPos.x, e.clientX)
      const top = Math.min(this._mouseDownPos.y, e.clientY)
      const width = Math.abs(e.clientX - this._mouseDownPos.x)
      const height = Math.abs(e.clientY - this._mouseDownPos.y)
      if (width > 2 && height > 2 && this._onBoxSelect) {
        const ids = this._getIdsInBox({ left, top, width, height })
        const centerScreen = { x: left + width / 2, y: top + height / 2 }
        this._onBoxSelect(ids, centerScreen)
      }
      this._isBoxSelecting = false
      this._isDragging = false
      return
    }

    if (this._isDragging) return

    const canvas = this._sm.renderer.domElement
    const rect = canvas.getBoundingClientRect()
    const ndc = new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1
    )

    this.raycaster.setFromCamera(ndc, this._sm.camera)
    this.raycaster.layers.set(0)

    const intersects = this.raycaster.intersectObjects(this._sm.scene.children, true)

    // 设备连线：优先于对象（与「连接到…」点选目标冲突时由 shouldDeferConnectionPick 关闭）
    if (!this._shouldDeferConnectionPick?.() && this._onConnectionClick) {
      for (const hit of intersects) {
        if (hit.object.parent?.name === '__ground__' || hit.object.name === '__ground__') continue
        const cid = hit.object.userData.connectionId as string | undefined
        if (cid) {
          this._onConnectionClick(cid, e)
          return
        }
      }
    }

    // 过滤有效命中并解析 objectId
    const validHits: { hit: THREE.Intersection; id: string }[] = []
    for (const hit of intersects) {
      if (hit.object.userData.interactable === false) continue
      if (hit.object.parent?.name === '__ground__' || hit.object.name === '__ground__') continue
      const id = hit.object.userData.objectId as string | undefined
      if (id) validHits.push({ hit, id })
    }

    // 命中优先级应与 raycaster 返回顺序一致，避免“点击 A 却选中 B”。
    // 这里不再按 objectId 做二次排序。
    const hit = validHits.length > 0 ? validHits[0] : null

    let found = false
    if (hit) {
      const id = hit.id
      if (!e.shiftKey) {
        if (this._currentId && this._currentId !== id) {
          setHighlight(this._currentId, false)
        }
        this._currentId = id
        setHighlight(id, true)
      }
      const screenPos = { x: e.clientX, y: e.clientY }
      this._onSelect(id, screenPos, e.shiftKey, e)
      found = true
    }

    if (!found) {
      if (!e.shiftKey) {
        if (this._currentId) setHighlight(this._currentId, false)
        this._currentId = null
      }
      this._onSelect(null, null, e.shiftKey, e)
    }
  }

  setCurrentId(id: string | null) {
    if (this._currentId && this._currentId !== id) {
      setHighlight(this._currentId, false)
    }
    this._currentId = id
  }

  getCurrentId() {
    return this._currentId
  }

  dispose() {
    const canvas = this._sm.renderer.domElement
    canvas.removeEventListener('mousedown', this._onMouseDown)
    canvas.removeEventListener('mouseup', this._onMouseUp)
    canvas.removeEventListener('mousemove', this._onMouseMove)
  }
}
